"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { ExternalLink, Loader2, RefreshCw, Unplug } from "lucide-react";
import { toast } from "@/lib/toast";
import { relativeTime } from "@/lib/utils";
import { BrandIcon } from "@/components/tailwind/ui/BrandIcon";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import {
  listSyncedDatabases,
  refreshSyncedDatabase,
  removeSyncedDatabase,
  type SyncedDatabaseSummary,
} from "@/services/syncedDatabaseService";

const CONNECTOR_LABEL: Record<SyncedDatabaseSummary["connectorId"], string> = {
  jira: "Jira",
  gitlab: "GitLab",
  asana: "Asana",
};

/**
 * Management panel for synced databases: list, manual refresh, stop.
 *
 * Creation lives in the Import modal ("Keep in sync"); this panel is where
 * the ongoing relationship is visible. Its one hard rule mirrors the
 * server's: stopping a sync KEEPS the database and rows, and every string
 * here says so — "Stop syncing", never "delete".
 */
export default function SyncedDatabasesSection() {
  const router = useRouter();
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?._id || "";

  const [loading, setLoading] = useState(true);
  const [syncs, setSyncs] = useState<SyncedDatabaseSummary[]>([]);
  /** id of the row with an in-flight refresh or stop, to disable its buttons. */
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setLoading(true);
    listSyncedDatabases(workspaceId).then((res) => {
      if (cancelled) return;
      setSyncs(res?.syncs ?? []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const handleRefresh = useCallback(
    async (sync: SyncedDatabaseSummary) => {
      setBusyId(sync.id);
      const run = await refreshSyncedDatabase(sync.id, workspaceId);
      setBusyId(null);
      if (!run) {
        toast.error("Could not refresh — try again in a moment.");
        return;
      }

      // Fold the run result back into the row so the status line is honest
      // without a refetch.
      setSyncs((prev) =>
        prev.map((s) =>
          s.id === sync.id
            ? {
                ...s,
                lastSyncedAt: new Date().toISOString(),
                lastRunStatus: run.status,
                lastRunMessage: run.message,
                truncated: run.truncated,
              }
            : s,
        ),
      );

      if (run.status === "error") {
        toast.error(run.message || "The sync failed — see the row for details.");
      } else if (run.status === "partial" && run.message) {
        // A capped run is a real state; the server's wording explains what
        // was and wasn't mirrored. Collapsing this into "Synced" would lie.
        toast.warning(run.message, { duration: 10000 });
      } else {
        toast.success(
          `${sync.remoteName}: ${run.created} added, ${run.updated} updated`,
        );
      }
    },
    [workspaceId],
  );

  const handleStop = useCallback(
    async (sync: SyncedDatabaseSummary) => {
      // House confirm pattern (window.confirm, two lines) — and the wording
      // carries the server's guarantee: content survives, only syncing stops.
      if (
        !window.confirm(
          `Stop syncing "${sync.remoteName}"?\n\nThe database and all its rows stay in your workspace — they just stop updating from ${CONNECTOR_LABEL[sync.connectorId]}.`,
        )
      )
        return;

      setBusyId(sync.id);
      const ok = await removeSyncedDatabase(sync.id, workspaceId);
      setBusyId(null);
      if (!ok) {
        toast.error("Could not stop the sync — try again in a moment.");
        return;
      }
      setSyncs((prev) => prev.filter((s) => s.id !== sync.id));
      toast.success(`${sync.remoteName} stopped syncing — the database was kept.`);
    },
    [workspaceId],
  );

  const statusLine = (sync: SyncedDatabaseSummary) => {
    if (sync.lastRunStatus === "error") {
      return (
        <span className="text-red-600 dark:text-red-400">
          Sync failed{sync.lastRunMessage ? ` — ${sync.lastRunMessage}` : ""}
        </span>
      );
    }
    if (sync.lastRunStatus === "partial") {
      return (
        <span className="text-amber-600 dark:text-amber-500">
          Partially synced{sync.lastRunMessage ? ` — ${sync.lastRunMessage}` : ""}
        </span>
      );
    }
    return (
      <span>
        {sync.lastSyncedAt ? `Synced ${relativeTime(sync.lastSyncedAt)}` : "Not synced yet"}
      </span>
    );
  };

  // Nothing to manage and nothing loading — say where syncs come from
  // instead of rendering a bare empty box.
  return (
    <div>
      <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
        Synced databases
      </p>
      <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3 leading-snug">
        Databases that mirror a Jira, GitLab or Asana project, one-way. Stopping a
        sync keeps the database and its rows.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : syncs.length === 0 ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400 py-4">
          No synced databases yet. Create one from Import → “Keep in sync”.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {syncs.map((sync) => {
            const busy = busyId === sync.id;
            return (
              <div
                key={sync.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-component"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-zinc-50 dark:bg-zinc-800/60">
                  <BrandIcon id={sync.connectorId} alt={CONNECTOR_LABEL[sync.connectorId]} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                    {sync.remoteName}
                  </p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                    {CONNECTOR_LABEL[sync.connectorId]} · {statusLine(sync)}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    title="Open the database"
                    onClick={() => router.push(`/notes/${sync.pageId}`)}
                    className="p-2 rounded-lg text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover-component transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    title="Sync now"
                    disabled={busy}
                    onClick={() => handleRefresh(sync)}
                    className="p-2 rounded-lg text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover-component transition-colors disabled:opacity-50"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    title="Stop syncing (keeps the database)"
                    disabled={busy}
                    onClick={() => handleStop(sync)}
                    className="p-2 rounded-lg text-zinc-400 hover:text-red-600 dark:hover:text-red-400 hover-component transition-colors disabled:opacity-50"
                  >
                    <Unplug className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
